/**
 * BillingCard Component
 *
 * Shows the user's current plan (Free / Starter), trial countdown,
 * monthly token usage, and actions to upgrade or manage subscription.
 * Includes cancellation flow with retention modal and reactivation.
 */

import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Zap, Loader2, AlertTriangle, ExternalLink, ArrowUpRight, FileText, X } from 'lucide-react';
import {
  getBillingStatus,
  createCheckoutSession,
  getBillingPortalUrl,
  getBillingInvoices,
  cancelSubscription,
  reactivateSubscription,
} from '../api/billing';
import type { BillingInvoice } from '../api/billing';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function InvoiceList({ invoices, isLoading }: { invoices: BillingInvoice[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-1 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 bg-surface-hover-light dark:bg-surface-hover-dark rounded" />
        ))}
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark italic">
        No invoices yet.
      </p>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-text-secondary-light dark:text-text-secondary-dark border-b border-border-subtle-light dark:border-border-subtle-dark">
          <th className="pb-1.5 font-medium">Date</th>
          <th className="pb-1.5 font-medium">Amount</th>
          <th className="pb-1.5 font-medium">Status</th>
          <th className="pb-1.5 font-medium text-right">PDF</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((inv) => (
          <tr
            key={inv.id}
            className="border-b border-border-subtle-light dark:border-border-subtle-dark last:border-0"
          >
            <td className="py-1.5 text-text-primary-light dark:text-text-primary-dark tabular-nums">
              {inv.date ? new Date(inv.date).toLocaleDateString() : '—'}
            </td>
            <td className="py-1.5 font-mono text-text-primary-light dark:text-text-primary-dark tabular-nums">
              ${inv.amount.toFixed(2)}
            </td>
            <td className="py-1.5">
              <span
                className={`capitalize text-xs font-medium ${
                  inv.status === 'paid'
                    ? 'text-green-600 dark:text-green-400'
                    : inv.status === 'open'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-text-secondary-light dark:text-text-secondary-dark'
                }`}
              >
                {inv.status ?? '—'}
              </span>
            </td>
            <td className="py-1.5 text-right">
              {inv.pdfUrl ? (
                <a
                  href={inv.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                  aria-label="Download invoice PDF"
                >
                  <FileText className="w-3.5 h-3.5" />
                  PDF
                </a>
              ) : (
                <span className="text-text-tertiary-light dark:text-text-tertiary-dark">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Retention modal shown before confirming cancellation */
function CancelModal({
  cancelAt,
  onConfirm,
  onDismiss,
  isLoading,
}: {
  cancelAt: string | null;
  onConfirm: () => void;
  onDismiss: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 max-w-sm w-full space-y-4 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
            Cancel your subscription?
          </h3>
          <button
            onClick={onDismiss}
            className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
          <p>
            Your subscription will remain active
            {cancelAt ? ` until ${new Date(cancelAt).toLocaleDateString()}` : ' until the end of the current billing period'}.
            After that, you'll be moved to the free tier (50 AI operations/month).
          </p>
          <p>You can reactivate any time before the period ends.</p>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Cancelling…
              </>
            ) : (
              'Yes, cancel at period end'
            )}
          </button>
          <button
            onClick={onDismiss}
            disabled={isLoading}
            className="w-full px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors disabled:opacity-50"
          >
            Keep my subscription
          </button>
        </div>
      </div>
    </div>
  );
}

export function BillingCard() {
  const queryClient = useQueryClient();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing-status'],
    queryFn: getBillingStatus,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });

  // Invalidate billing cache immediately when returning from a successful checkout.
  // Stripe redirects back to /settings?tab=billing&checkout=success — without this,
  // the user would see the old free-tier state for up to 2 minutes after upgrading.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      void queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      // Clean up the URL param without a page reload
      params.delete('checkout');
      const newSearch = params.toString();
      window.history.replaceState(null, '', newSearch ? `?${newSearch}` : window.location.pathname);
    }
  }, [queryClient]);

  const {
    data: invoicesData,
    isLoading: isInvoicesLoading,
  } = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: getBillingInvoices,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    enabled: data?.stripeConfigured === true,
  });

  const handleUpgrade = async () => {
    setIsCheckingOut(true);
    setActionError(null);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch (err: any) {
      setActionError(err.message || 'Failed to start checkout');
      setIsCheckingOut(false);
    }
  };

  const handlePortal = async () => {
    setIsOpeningPortal(true);
    setActionError(null);
    try {
      const { url } = await getBillingPortalUrl();
      window.open(url, '_blank', 'noopener');
    } catch (err: any) {
      setActionError(err.message || 'Failed to open portal');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    setActionError(null);
    try {
      await cancelSubscription();
      await queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      setIsCancelModalOpen(false);
    } catch (err: any) {
      setActionError(err.message || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    setActionError(null);
    try {
      await reactivateSubscription();
      await queryClient.invalidateQueries({ queryKey: ['billing-status'] });
    } catch (err: any) {
      setActionError(err.message || 'Failed to reactivate subscription');
    } finally {
      setIsReactivating(false);
    }
  };

  const isStarter = data?.subscriptionTier === 'starter';
  const inTrial = data?.trialActive === true;
  const trialExpired = data?.subscriptionTier === 'free' && data?.trialActive === false;
  const isPendingCancellation = isStarter && !!data?.cancelAt;

  return (
    <>
      {isCancelModalOpen && (
        <CancelModal
          cancelAt={data?.cancelAt ?? null}
          onConfirm={handleCancelConfirm}
          onDismiss={() => setIsCancelModalOpen(false)}
          isLoading={isCancelling}
        />
      )}

      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Plan &amp; Billing</h2>
        </div>

        {isLoading && (
          <div className="space-y-2 animate-pulse">
            <div className="h-6 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-28" />
            <div className="h-4 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-40" />
            <div className="h-4 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-52" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Unable to load billing info.</p>
        )}

        {data && !isLoading && (
          <>
            {/* Plan badge */}
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                  isStarter
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'bg-surface-hover-light dark:bg-surface-hover-dark text-text-secondary-light dark:text-text-secondary-dark'
                }`}
              >
                <Zap className="w-3.5 h-3.5" />
                {isStarter ? 'Starter' : 'Free'}
              </span>

              {/* Trial badge */}
              {inTrial && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  Trial · {data.trialDaysLeft} {data.trialDaysLeft === 1 ? 'day' : 'days'} left
                </span>
              )}

              {/* Trial expired warning */}
              {trialExpired && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Trial ended
                </span>
              )}

              {/* Pending cancellation notice */}
              {isPendingCancellation && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Cancels {new Date(data.cancelAt!).toLocaleDateString()}
                </span>
              )}
            </div>

            {/* Token usage */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary-light dark:text-text-secondary-dark">AI tokens this month</span>
                <span className="font-mono font-medium text-text-primary-light dark:text-text-primary-dark tabular-nums">
                  {formatTokens(data.monthlyUsage.totalTokens)}
                </span>
              </div>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {formatTokens(data.monthlyUsage.inputTokens)} in &middot;{' '}
                {formatTokens(data.monthlyUsage.outputTokens)} out &middot;{' '}
                {data.monthlyUsage.opCount} {data.monthlyUsage.opCount === 1 ? 'operation' : 'operations'}
              </p>
            </div>

            {/* Action error */}
            {actionError && (
              <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p>
            )}

            {/* Stripe not configured note */}
            {!data.stripeConfigured && (
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark italic">
                Billing not enabled on this server.
              </p>
            )}

            {/* Actions */}
            {data.stripeConfigured && (
              <div className="flex flex-col gap-2 pt-1">
                {!isStarter && (
                  <button
                    onClick={handleUpgrade}
                    disabled={isCheckingOut}
                    className="w-full px-4 py-2.5 bg-primary-700 hover:bg-primary-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isCheckingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        <ArrowUpRight className="w-4 h-4" />
                        Upgrade to Starter — $2/mo + usage
                      </>
                    )}
                  </button>
                )}

                {/* Reactivate button — shown when cancellation is pending */}
                {isPendingCancellation && (
                  <button
                    onClick={handleReactivate}
                    disabled={isReactivating}
                    className="w-full px-4 py-2.5 bg-primary-700 hover:bg-primary-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isReactivating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Reactivating…
                      </>
                    ) : (
                      'Keep my subscription'
                    )}
                  </button>
                )}

                <button
                  onClick={handlePortal}
                  disabled={isOpeningPortal}
                  className="w-full px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {isOpeningPortal ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  {isStarter ? 'Manage subscription' : 'View billing'}
                </button>

                {/* Cancel button — shown for active Starter subscribers only */}
                {isStarter && !isPendingCancellation && (
                  <button
                    onClick={() => setIsCancelModalOpen(true)}
                    className="w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Cancel subscription
                  </button>
                )}
              </div>
            )}

            {/* Invoice history */}
            {data.stripeConfigured && (
              <div className="pt-2 space-y-2">
                <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                  Invoice History
                </h3>
                <InvoiceList
                  invoices={invoicesData?.invoices ?? []}
                  isLoading={isInvoicesLoading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
